import { Col, Row } from "antd";
import { User } from "types/";
import { ProjectAnalytics } from "./ProjectAnalytics";
import { ProjectDetailsCard } from "./ProjectDetailsCard";
import { ProjectPreviewCard } from "./ProjectPreviewCard";
import { ProjectStatusCard } from "./ProjectStatusCard";
import { ProjectPowerFeatures } from "./ProjectPowerFeatures";
import { ProjectQuickEdits } from "./ProjectQuickEdits";
import { ProjectShare } from "./ProjectShare";
import { ProjectShareExport } from "./ProjectShareExport";
import ProjectUnpublish from "./ProjectUnpublish";
import { isProjectHeyGenius } from "../helpers/helper";
import { ProjectType } from "../types";
import { InstanceStatus } from "./types";
import { MediaProjectResponse, ProjectMediaResponse } from "pages/media/types";
import { FeatureFlags } from "@/constants";
import { useFeatureFlag } from "hooks/useFeature/";
import { ProjectPageNew } from "./ProjectPageNew";

export type ProjectPageDetails = {
  project: MediaProjectResponse;
  projectId: string;
  instanceId: string;
  storyboardId: string;
  organizationSlug?: string;
  media?: ProjectMediaResponse;
  owner?: User;
  isLive: boolean;
  isDocHeyGenius: boolean;
  isAudioOnly: boolean;
  publishedPublicationUrl: string;
  disablePublish: boolean;
  creationDate: string;
  isUnpublishing: boolean;
  /** Live, but a later edit has staled the published version. */
  isPublicationOutOfDate: boolean;
  /** The generated media no longer matches the project's current content. */
  isMediaOutOfDate: boolean;
  /** Combined visibility gate for the out-of-date widget/compact button — `isMediaOutOfDate || isPublicationOutOfDate`. */
  isOutOfDate: boolean;
  /** Whether the queries the two out-of-date flags above depend on are still settling. */
  isOutOfDateStatusLoading: boolean;
  /** Resolves to whether the republish actually succeeded — used to gate clearing the out-of-date widget's dismissal. */
  onRepublish: () => Promise<boolean>;
  /** Resolves to whether the regenerate/approve actually succeeded — used to gate clearing the out-of-date widget's dismissal. */
  onRegenerate: () => Promise<boolean>;
};

type ProjectPageProps = {
  projectDetails: ProjectPageDetails;
  onUnpublish: () => Promise<void>;
  onPublish: () => Promise<{ quotaExceeded: boolean }>;
  refetchProjectDetail: () => void;
};

export const ProjectPage = ({
  projectDetails,
  onUnpublish,
  onPublish,
  refetchProjectDetail,
}: ProjectPageProps) => {
  const {
    project,
    projectId,
    instanceId,
    storyboardId,
    organizationSlug,
    media,
    owner,
    isLive,
    isDocHeyGenius,
    isAudioOnly,
    publishedPublicationUrl,
    disablePublish,
    creationDate,
    isUnpublishing,
  } = projectDetails;

  const isNewProjectLayout = useFeatureFlag(
    FeatureFlags.NewProjectDetailLayout
  );

  if (isNewProjectLayout) {
    return (
      <ProjectPageNew
        projectDetails={projectDetails}
        onUnpublish={onUnpublish}
        onPublish={onPublish}
        refetchProjectDetail={refetchProjectDetail}
      />
    );
  }

  return (
    <div>
      {isLive && !isProjectHeyGenius(project.type) && (
        <ProjectShare
          projectUrl={publishedPublicationUrl}
          projectId={projectId}
          instanceId={instanceId}
        />
      )}

      {isLive && (
        <ProjectUnpublish
          projectType={project.type as ProjectType}
          onUnpublish={() => void onUnpublish()}
          isMutating={isUnpublishing}
        />
      )}
      <Row gutter={[24, 24]}>
        <Col xs={24} md={24} lg={24} xl={16}>
          <>
            <Row style={{ marginBottom: 24 }}>
              <Col span={24}>
                <ProjectDetailsCard
                  key={`${instanceId}-${media?.id}`}
                  disablePublish={disablePublish}
                  projectDetails={{
                    projectId,
                    instanceId,
                    project,
                    storyboardId,
                    organizationSlug,
                    instanceSlug: media?.instance_slug,
                    mediaId: media?.id,
                    mediaCreated: media?.created,
                    videoUrl: media?.url,
                    mediaStatus: media?.status as InstanceStatus,
                    isLive,
                    isDocHeyGenius,
                    owner,
                    publishedPublicationUrl,
                    organizationId: project.organization_id,
                  }}
                />
              </Col>
            </Row>
            <Row style={{ marginBottom: 24 }}>
              <Col span={24}>
                <ProjectPreviewCard
                  key={`preview-${instanceId}-${media?.id}`}
                  refetchProjectDetail={refetchProjectDetail}
                  projectDetails={{
                    projectId,
                    instanceId,
                    project,
                    storyboardId,
                    organizationSlug,
                    instanceSlug: media?.instance_slug,
                    mediaId: media?.id,
                    mediaCreated: media?.created,
                    videoUrl: media?.url,
                    mediaStatus: media?.status as InstanceStatus,
                    isLive,
                    isDocHeyGenius,
                    owner,
                    publishedPublicationUrl,
                  }}
                  backgroundImg="/images/libertify_poster.png"
                />
              </Col>
            </Row>
            <Row style={{ marginBottom: 24 }}>
              <Col span={24}>
                <ProjectStatusCard
                  mediaStatus={media?.status as InstanceStatus}
                  isLive={isLive}
                />
              </Col>
            </Row>
          </>
        </Col>
        <Col xs={24} md={24} lg={24} xl={8}>
          <Row style={{ marginBottom: 24 }}>
            <ProjectAnalytics
              projectDetails={{
                projectId,
                instanceId,
                storyboardId,
                isLive,
                creationDate,
              }}
              disablePublish={disablePublish}
            />
          </Row>
          <Row style={{ marginBottom: 24 }}>
            <ProjectShareExport
              projectDetails={{
                projectId: projectId || "",
                projectType: project.type,
                mediaId: media?.id ?? undefined,
                mediaUrl: media?.url,
                instanceId,
                instanceSlug: media?.instance_slug ?? undefined,
                organizationSlug,
                mediaStatus: media?.status,
                isLive,
                tags: project.tags,
                isAudioOnly,
              }}
              disableOpenExperience={disablePublish}
            />
          </Row>
          <Row style={{ marginBottom: 24 }}>
            <ProjectQuickEdits
              projectId={projectId || ""}
              projectType={project.type}
              isLive={isLive}
              storyboardId={storyboardId}
              instanceId={instanceId}
              mediaId={media?.id}
            />
          </Row>
          <Row>
            <ProjectPowerFeatures
              projectId={projectId || ""}
              isLive={isLive}
              storyboardId={storyboardId}
              instanceId={instanceId}
            />
          </Row>
        </Col>
      </Row>
    </div>
  );
};
